/**
 * Crisp identity query
 *
 * Fetches { email, signature, nickname } from `/api/support/crisp-identity`
 * so the client can pass an HMAC-verified identity to Crisp's web SDK.
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../query-keys';

export interface CrispIdentity {
  email: string;
  signature: string;
  nickname?: string;
}

async function fetchCrispIdentity(): Promise<CrispIdentity | null> {
  const response = await fetch('/api/support/crisp-identity', {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (response.status === 401 || response.status === 503) return null;
  if (!response.ok) {
    throw new Error(`Crisp identity request failed: ${response.status}`);
  }
  return response.json();
}

export function useCrispIdentity(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.support.crispIdentity(),
    queryFn: fetchCrispIdentity,
    enabled,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });
}
